import { useState } from "react";
import { Send } from "lucide-react";

type Status = "idle" | "sending" | "sent" | "error";

export function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    setStatus("sending");
    setError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(json.error || "Something went wrong.");
      setStatus("sent");
      form.reset();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  const field =
    "mt-2 w-full border-b border-border bg-transparent pb-2 text-sm outline-none transition-colors duration-300 focus:border-[var(--terracotta)] focus-visible:border-[var(--terracotta)]";
  const label = "text-[11px] uppercase tracking-[0.16em] text-muted-foreground";

  if (status === "sent") {
    return (
      <div className="border border-border p-8">
        <p className="display text-3xl text-primary">Message sent.</p>
        <p role="status" className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Thank you — Shirley will be in touch soon. A confirmation is on its way to your inbox.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-7">
      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor="cf-name">
            Name
          </label>
          <input
            id="cf-name"
            name="name"
            autoComplete="name"
            required
            maxLength={120}
            className={field}
          />
        </div>
        <div>
          <label className={label} htmlFor="cf-email">
            Email
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            maxLength={200}
            className={field}
          />
        </div>
      </div>
      <div>
        <label className={label} htmlFor="cf-phone">
          Phone (optional)
        </label>
        <input
          id="cf-phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          maxLength={40}
          className={field}
        />
      </div>
      <div>
        <label className={label} htmlFor="cf-message">
          What would you like framed?
        </label>
        <textarea
          id="cf-message"
          name="message"
          required
          rows={4}
          maxLength={4000}
          className={field + " resize-none"}
        />
      </div>
      <input
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />
      {status === "error" && (
        <p role="alert" className="text-sm text-[var(--terracotta)]">
          {error}
        </p>
      )}
      <button type="submit" className="btn-primary" disabled={status === "sending"}>
        <Send className="size-3.5" />
        {status === "sending" ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
